// Loads the non-English recruiter bundles. This module must stay free of
// i18next (and React): the app entry imports it to warm the cached language's
// chunks in parallel with the app chunk, before i18next has loaded. Only the
// recruiter app and the internal app's lazy i18n chunk reach it, so the glob
// below never lands in the candidate builds or the internal shell.
import {
  sessionStore,
  SessionStorageKey,
  storage,
  StorageKey,
} from 'utils/typedStorage';

import { getLocaleBundles } from '../i18nLocaleLoader';
import {
  bundleLanguagesFor,
  isRecruiterLanguage,
  normalizeRecruiterLanguage,
  RECRUITER_ADMIN_NAMESPACES,
  type RecruiterLanguage,
} from './languages';

/** How long a language switch may wait on its bundles before staying English. */
export const RECRUITER_LANGUAGE_TIMEOUT_MS = 2000;

export type RecruiterResources = Record<string, string>;

export interface RecruiterBundle {
  language: RecruiterLanguage;
  namespace: string;
  resources: RecruiterResources;
}

type BundleImporter = () => Promise<{ default: RecruiterResources }>;

// English is statically imported by each namespace module, so only the other
// languages are lazy. Startup, errors and staff namespaces load as groups;
// administrative namespaces load individually because shared job filters use adminAts.
// vite.config.ts (recruiterLocaleChunk) gives each group of a language its own
// chunk, so a group costs one request however many namespaces it has.
const startupImporters = import.meta.glob<{ default: RecruiterResources }>([
  '../locales/*/recruiter/*.json',
  '!../locales/en/**',
  '!../locales/*/recruiter/errors.json',
  '!../locales/*/recruiter/devtools.json',
  '!../locales/*/recruiter/internalTools.json',
  '!../locales/*/recruiter/admin*.json',
]);
const errorImporters = import.meta.glob<{ default: RecruiterResources }>([
  '../locales/*/recruiter/errors.json',
  '!../locales/en/**',
]);
const staffImporters = import.meta.glob<{ default: RecruiterResources }>([
  '../locales/*/recruiter/devtools.json',
  '../locales/*/recruiter/internalTools.json',
  '!../locales/en/**',
]);

const adminImporters = import.meta.glob<{ default: RecruiterResources }>([
  '../locales/*/recruiter/admin*.json',
  '!../locales/en/**',
]);

const BUNDLE_PATH_RE = /\/locales\/([^/]+)\/recruiter\/([^/]+)\.json$/;

export interface BundleSource {
  language: string;
  namespace: string;
  load: BundleImporter;
}

export interface RecruiterBundleLoadOptions {
  /** Start a new attempt instead of reusing a request that may have stalled. */
  retry?: boolean;
}

/** Parses glob paths into (language, namespace, importer) triples. */
export const parseBundleSources = (
  importers: Record<string, BundleImporter>
): BundleSource[] =>
  Object.entries(importers).flatMap(([filePath, load]) => {
    const [, language, namespace] = BUNDLE_PATH_RE.exec(filePath) ?? [];
    return language !== undefined && namespace !== undefined
      ? [{ language, namespace, load }]
      : [];
  });

const createBundleLoader = (sources: BundleSource[]) => {
  const cache = new Map<RecruiterLanguage, Promise<RecruiterBundle[]>>();

  const fetchLanguage = (
    language: RecruiterLanguage
  ): Promise<RecruiterBundle[]> => {
    let pending = cache.get(language);
    if (!pending) {
      pending = Promise.all(
        sources
          .filter((source) => source.language === language)
          .map(async (source) => ({
            language,
            namespace: source.namespace,
            resources: (await source.load()).default,
          }))
      );
      cache.set(language, pending);
      // Drop failures so a later attempt can retry.
      pending.catch(() => {
        // An older, timed-out request must not evict a newer retry.
        if (cache.get(language) === pending) cache.delete(language);
      });
    }
    return pending;
  };

  /**
   * Every namespace of the group for `language` plus the language it falls back to, in
   * fallback-first order. Rejects if any file fails, so the caller can keep
   * English rather than show a half-translated UI.
   */
  return async (
    language: RecruiterLanguage,
    { retry = false }: RecruiterBundleLoadOptions = {}
  ): Promise<RecruiterBundle[]> => {
    const languages = bundleLanguagesFor(language);
    if (retry) {
      for (const code of languages) cache.delete(code);
    }
    const groups = await Promise.all(languages.map(fetchLanguage));
    return groups.flat();
  };
};

/** Builds a loader over explicit sources; exported for tests. */
export const createRecruiterBundleLoader = createBundleLoader;

/** The namespaces fetched when a recruiter language starts. */
export const loadRecruiterBundles = createBundleLoader(
  parseBundleSources(startupImporters)
);

/** The `errors` namespace, which only coded API error text reads. */
export const loadRecruiterErrorBundles = createBundleLoader(
  parseBundleSources(errorImporters)
);

/** The staff-only namespaces (dev and support tools). */
export const loadRecruiterStaffBundles = createBundleLoader(
  parseBundleSources(staffImporters)
);

const adminSources = parseBundleSources(adminImporters);
const adminLoaders = new Map<string, ReturnType<typeof createBundleLoader>>(
  RECRUITER_ADMIN_NAMESPACES.map((namespace) => [
    namespace,
    createBundleLoader(
      adminSources.filter((source) => source.namespace === namespace)
    ),
  ])
);

/** Load only registered administrative namespaces, sharing each language's pending fetch. */
export const loadRecruiterAdminBundles = async (
  language: RecruiterLanguage,
  options?: RecruiterBundleLoadOptions,
  namespaces: readonly string[] = RECRUITER_ADMIN_NAMESPACES
): Promise<RecruiterBundle[]> => {
  const groups = await Promise.all(
    namespaces.flatMap((namespace) => {
      const load = adminLoaders.get(namespace);
      return load ? [load(language, options)] : [];
    })
  );
  return groups.flat();
};

type NonEnglish = Exclude<RecruiterLanguage, 'en'>;

// Registering a dayjs locale is a side effect of importing it; Mantine's date
// components then resolve it by name from their `locale` prop.
const dayjsLocaleImporters: Record<NonEnglish, () => Promise<unknown>> = {
  fr: () => import('dayjs/locale/fr'),
  es: () => import('dayjs/locale/es-mx'),
  'es-ES': () => import('dayjs/locale/es'),
  de: () => import('dayjs/locale/de'),
  nl: () => import('dayjs/locale/nl'),
  'nl-BE': () => import('dayjs/locale/nl-be'),
};

/** Imports (and so registers) the dayjs locale for `language`. */
export const loadRecruiterDayjsLocale = async (
  language: RecruiterLanguage
): Promise<void> => {
  if (language === 'en') return;
  await dayjsLocaleImporters[language]();
};

export const needsRecruiterLocaleFetch = (
  language: RecruiterLanguage
): boolean => language !== 'en';

/** Support's tab-local override, else the last session's cached language or English. */
export const readCachedRecruiterLanguage = (): RecruiterLanguage => {
  const override = sessionStore.get(SessionStorageKey.SupportUiLanguage);
  if (
    storage.getDynamic('isSupport') === 'true' &&
    isRecruiterLanguage(override)
  ) {
    return override;
  }
  return normalizeRecruiterLanguage(storage.get(StorageKey.UiLanguage));
};

export const writeCachedRecruiterLanguage = (
  language: RecruiterLanguage
): void => {
  if (language === 'en') {
    storage.remove(StorageKey.UiLanguage);
  } else {
    storage.set(StorageKey.UiLanguage, language);
  }
};

/**
 * Best-effort warmup for the entry. Never rejects; registration surfaces
 * real failures.
 */
export const prefetchRecruiterLocale = async (
  language: RecruiterLanguage
): Promise<void> => {
  if (!needsRecruiterLocaleFetch(language)) return;
  // The shared candidate `common` bundle and the dayjs locale are applied with
  // the recruiter bundles, so they warm alongside them.
  await Promise.all([
    loadRecruiterBundles(language),
    getLocaleBundles(language),
    loadRecruiterDayjsLocale(language),
  ]).catch(() => {
    // swallow-ok: Startup locale warmup is repeated by applyRecruiterLanguage; its required load records failure, shows English, and exposes retryRecruiterLanguage.
    // Registration retries and falls back to English.
  });
};

// One-way handshake with recruiter-i18n/index.ts, a bare promise because this
// module cannot import i18next. Resolved once, by markInitialRecruiterLanguageApplied.
let resolveInitialApplied: () => void = () => {};
const initialApplied = new Promise<void>((resolve) => {
  resolveInitialApplied = resolve;
});

/**
 * Resolves once the cached language is registered and active, so the entry can
 * hold the first render instead of painting English and swapping a beat later.
 */
export const whenInitialRecruiterLanguageApplied = (): Promise<void> =>
  initialApplied;

export const markInitialRecruiterLanguageApplied = (): void => {
  resolveInitialApplied();
};
